import React from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import Network from './general/Network'
import useEnvironmentStats from '../hooks/useEnvironmentStats'
import { parseName } from '../utils/math-utils'
import { useBookmarks } from '../context/BookmarksContext'
import { GlassCard } from '../styles/ds'

interface EnvironmentCardProps {
  namespace: string
  mimicId: string
  chainIds: number[]
}

const EnvironmentCard: React.FC<EnvironmentCardProps> = ({ namespace, mimicId, chainIds }) => {
  const navigate = useNavigate()
  const { data: stats, isLoading } = useEnvironmentStats(mimicId)
  const { isBookmarked, toggle } = useBookmarks()

  const handleConfigClick = () => navigate(`/dashboard/environments/${mimicId}/`)

  const handleChangeOnchainClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/dashboard/environments/config/${mimicId}/`)
  }

  const handleConfigButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleConfigClick()
  }

  const handleCopyMimicId = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(mimicId)
    toast.success('Copied!')
  }

  return (
    <Card onClick={handleConfigClick}>
      <CardTop>
        <MimicId>
          <MimicIdText>{mimicId.substring(0, 10)}...{mimicId.substring(mimicId.length - 8)}</MimicIdText>
          <CopyBtn onClick={handleCopyMimicId} title="Copy Mimic ID">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </CopyBtn>
        </MimicId>
        <BookmarkBtn
          $active={isBookmarked(mimicId)}
          onClick={(e) => { e.stopPropagation(); toggle(mimicId, namespace) }}
          title={isBookmarked(mimicId) ? 'Remove bookmark' : 'Bookmark'}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={isBookmarked(mimicId) ? '#a89aff' : 'none'} stroke={isBookmarked(mimicId) ? '#a89aff' : '#555'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </BookmarkBtn>
      </CardTop>

      <EnvironmentName>{parseName(namespace)}</EnvironmentName>

      <CardMid>
        <MetaLabel>{chainIds.length} network{chainIds.length !== 1 ? 's' : ''}</MetaLabel>
        <NetworkStack>
          {chainIds.map((chainId, index) => (
            <Network key={`${chainId}-${index}`} network={chainId} small={true} />
          ))}
        </NetworkStack>
      </CardMid>

      <TxSection>
        <TxCount>
          {isLoading ? <Skeleton /> : <>{stats?.transactions24h || 0}</>}
        </TxCount>
        <TxLabel>txs in 24h</TxLabel>
      </TxSection>

      <CardFooter>
        <FooterBtn onClick={handleChangeOnchainClick} primary>Change Onchain</FooterBtn>
        <FooterBtn onClick={handleConfigButtonClick}>Config</FooterBtn>
      </CardFooter>
    </Card>
  )
}

const Card = styled(GlassCard)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 1px rgba(111,92,230,0.1), 0 24px 64px rgba(0,0,0,0.6);
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const MimicId = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const MimicIdText = styled.span`
  color: #a1a1a1;
  font-size: 11px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  letter-spacing: 0.02em;
`

const BookmarkBtn = styled.button<{ $active: boolean }>`
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; padding: 2px; cursor: pointer;
  color: ${({ $active }) => $active ? '#a89aff' : '#555'};
  transition: color 0.15s, transform 0.1s;
  &:hover { color: #a89aff; transform: scale(1.15); }
`

const CopyBtn = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: #777;
  transition: color 0.15s;

  &:hover { color: #888; }
`

const EnvironmentName = styled.h3`
  color: #ededed;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
  font-family: inherit;
`

const CardMid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`

const MetaLabel = styled.div`
  color: #a1a1a1;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 500;
`

const NetworkStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
  img { margin-right: 0 !important; }
`

const TxSection = styled.div`
  padding: 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
`

const TxCount = styled.div`
  color: #ededed;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  margin-bottom: 2px;
`

const TxLabel = styled.div`
  color: #a1a1a1;
  font-size: 11px;
`

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`

const Skeleton = styled.div`
  width: 60px;
  height: 28px;
  background: linear-gradient(90deg, #1a1a1a 25%, #222 50%, #1a1a1a 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
`

const CardFooter = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`

const FooterBtn = styled.button<{ primary?: boolean }>`
  flex: 1;
  height: 32px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;

  ${({ primary }) => primary ? `
    background: #6f5ce6;
    color: #fff;
    border: none;
    &:hover { background: #5a4bd4; }
  ` : `
    background: transparent;
    color: #a1a1a1;
    border: 1px solid rgba(255,255,255,0.08);
    &:hover { color: #a0a0a0; border-color: rgba(255,255,255,0.15); }
  `}
`

export default EnvironmentCard
